import type { Router } from 'vue-router'
import { nextTick } from 'vue'
import { i18n } from '@/i18n'
import { useA11yStore } from '@/stores/a11y'

/**
 * Titre de page, annonce vocale et déplacement du focus à chaque navigation.
 *
 * ─── Le problème ───
 *
 * Dans une application à page unique, changer d'écran ne recharge rien. Pour un
 * lecteur d'écran, il ne s'est donc RIEN passé : il continue de lire là où il
 * était, ou se tait. Et le focus reste sur le lien du menu qu'on vient
 * d'activer — la tabulation suivante continue dans le menu, pas dans le nouvel
 * écran. C'est le défaut d'accessibilité le plus répandu des applications Vue et
 * React, et il ne se voit jamais à la souris.
 *
 * Trois corrections, dans cet ordre :
 *
 *   1. `document.title` — c'est ce que la plupart des lecteurs d'écran énoncent
 *      au chargement d'une page, et ce qui nomme les onglets et l'historique du
 *      navigateur ;
 *   2. une annonce dans la région « live », parce que le changement de titre
 *      seul n'est PAS annoncé de façon fiable en navigation à page unique ;
 *   3. le focus déplacé sur `<main>`, pour que la tabulation reprenne dans le
 *      contenu.
 */

/** Titre de chaque route, en clé i18n. */
const TITRES: Record<string, string> = {
  login: 'auth.title',
  ai: 'nav.ai',
  'console.enveloppe.liste': 'console.liste.title',
  'console.enveloppe.nouvelle': 'console.envoi.title',
  'console.enveloppe.detail': 'console.detail.title',
  'console.horodatage.liste': 'console.horodatage.liste.title',
  'console.horodatage.piece': 'console.horodatage.piece.title',
  'console.modeles': 'console.modeles.title',
  'console.reglages': 'console.reglages.title',
  // On n'y arrive que par redirection — l'annonce et le titre n'en sont pas
  // moins exigés : c'est précisément l'écran qu'un utilisateur coupé doit
  // entendre nommer (revue E9-7 m1).
  'console.acces-retire': 'accesRetire.title',
  'superuser.organisations': 'nav.superuser.organisations',
  'superuser.plans': 'nav.superuser.plans',
  'superuser.services': 'nav.superuser.services',
  'superuser.service-etat': 'nav.superuser.service-etat',
  developpeurs: 'dev.title',
  'doc.api': 'dev.tabApi',
  'doc.mcp': 'dev.tabMcp',
  'not-found': 'notFound.title',
  // Le titre précis PAR ÉTAPE est posé par la vue elle-même : les transitions
  // du parcours restent dans cette route, hors portée de ce hook.
  'signataire.parcours': 'signataire.title',
}

export function installerAnnoncesRoute(router: Router): void {
  router.afterEach((to, depuis) => {
    const cle = typeof to.name === 'string' ? TITRES[to.name] : undefined
    const { t } = i18n.global
    const nomEcran = cle === undefined ? '' : t(cle)
    const nomApp = t('app.name')

    // Écran d'abord : dans un onglet réduit, seuls les premiers caractères sont
    // visibles, et c'est l'écran qui distingue les onglets — pas le nom de
    // l'application, identique partout.
    document.title = nomEcran === '' ? nomApp : `${nomEcran} · ${nomApp}`

    // Première navigation (`depuis.name` absent) : le navigateur vient de
    // charger le document, il l'annonce lui-même. Annoncer en plus donnerait un
    // doublon, et déplacer le focus retirerait à l'utilisateur son point de
    // départ naturel — dont le lien d'évitement.
    if (depuis.name === undefined) return

    const a11y = useA11yStore()
    if (nomEcran !== '') a11y.annoncer(t('a11y.announce.navigated', { page: nomEcran }))

    // Après le rendu du nouvel écran : avant, l'élément visé peut encore être
    // celui de l'écran précédent, voire absent pendant le chargement paresseux
    // du fragment.
    void nextTick(() => {
      const principal = document.getElementById('contenu-principal')
      // `preventScroll` : le comportement de défilement est déjà géré par
      // `scrollBehavior` du routeur, qui sait restituer la position au retour
      // arrière. Sans ce drapeau, le focus écraserait cette restitution.
      principal?.focus({ preventScroll: true })
    })
  })
}
